import type { TrainingSession } from '@cadencia/contracts';

/**
 * Cerrar la semana y preparar la siguiente antes de que llegue.
 *
 * DE DÓNDE SALE
 *
 * ADR-025 materializa las semanas de una en una, y hasta el 13/09/2026 eso
 * quería decir «cuando la semana llega»: un domingo sin sesiones pendientes no
 * había forma de ver la semana siguiente, y una sesión que se sabía que no se
 * iba a hacer se quedaba «pendiente» hasta caducar como perdida. El usuario
 * pidió las dos cosas, y eligió (13/09/2026) que la semana nueva se prepare al
 * cerrar la actual y **se rehaga** si luego se aplica un cambio que la habría
 * construido distinta.
 *
 * Aquí viven las tres preguntas que eso exige, sin Dexie: qué se puede cerrar,
 * cuándo está una semana terminada y cuándo sigue intacta una semana preparada.
 */

/** Estados que «dar por terminada la semana» convierte en `skipped`. */
const CERRABLES: ReadonlySet<TrainingSession['status']> = new Set(['planned', 'rescheduled']);

/** Estados que dicen que a la semana todavía le queda algo. */
const PENDIENTES: ReadonlySet<TrainingSession['status']> = new Set([
  'planned',
  'rescheduled',
  'in_progress',
]);

export interface WeekCloseAssessment {
  /** Las que pasarán a «No se hará». Incluye las atrasadas: tampoco se van a hacer. */
  closable: TrainingSession[];
  /**
   * Sesiones a medias. Bloquean el cierre: tienen series registradas, y darlas
   * por no hechas sería mentir; cerrarlas por el usuario, decidir por él.
   */
  inProgress: TrainingSession[];
}

function vivas(sessions: readonly TrainingSession[]): TrainingSession[] {
  return sessions.filter((sesion) => sesion.deletedAt === null);
}

export function assessWeekClose(sessions: readonly TrainingSession[]): WeekCloseAssessment {
  const deLaSemana = vivas(sessions);
  return {
    closable: deLaSemana
      .filter((sesion) => CERRABLES.has(sesion.status))
      .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate)),
    inProgress: deLaSemana.filter((sesion) => sesion.status === 'in_progress'),
  };
}

/**
 * Una semana está terminada cuando tiene sesiones y ninguna queda pendiente:
 * todas hechas, perdidas o dadas por no hechas.
 *
 * Sin sesiones NO está terminada: está sin materializar, que es otra cosa, y
 * tratarla como terminada prepararía la siguiente saltándose esta.
 */
export function weekIsFinished(sessions: readonly TrainingSession[]): boolean {
  const deLaSemana = vivas(sessions);
  return deLaSemana.length > 0 && deLaSemana.every((sesion) => !PENDIENTES.has(sesion.status));
}

/**
 * ¿Se puede rehacer una semana preparada de antemano?
 *
 * Solo si sigue exactamente como se creó: todas sus sesiones planificadas y
 * ninguna con fecha anterior a hoy. En cuanto hay una empezada, hecha, perdida o
 * saltada, la semana ya se está viviendo, y rehacerla borraría eso. Una sesión
 * planificada de ayer tampoco: ese día pasó y no es lo mismo que uno por venir.
 */
export function canRebuildPreparedWeek(
  sessions: readonly TrainingSession[],
  today: string,
): boolean {
  const deLaSemana = vivas(sessions);
  return (
    deLaSemana.length > 0 &&
    deLaSemana.every((sesion) => sesion.status === 'planned' && sesion.scheduledDate >= today)
  );
}
